import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTitle } from '../../../context/TitleContext';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaArrowLeft, FaClock } from 'react-icons/fa';
import { showErrorToast } from '../../../shared/utils/toastHelper';
import apiClient from '../../../shared/utils/apiConfig';

const DoctorWorkDays = () => {
    const { clinicId } = useParams();
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    
    const [workDays, setWorkDays] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setTitle('أيام العمل');
        fetchWorkDays();
    }, [setTitle, clinicId]);

    const fetchWorkDays = async () => {
        try {
            setLoading(true);
            
            const response = await apiClient.get(`/doctor_dash/doctor_work_days?clinic_id=${clinicId}`);
            
            const data = response.data?.result || response.data?.data || response.data || [];
            setWorkDays(Array.isArray(data) ? data : []);
        } catch (error) {
            showErrorToast('فشل في جلب أيام العمل');
        } finally {
            setLoading(false);
        }
    };

    const getDayNameInArabic = (day) => {
        const daysMap = {
            saturday: 'السبت',
            sunday: 'الأحد',
            monday: 'الإثنين',
            tuesday: 'الثلاثاء',
            wednesday: 'الأربعاء',
            thursday: 'الخميس',
            friday: 'الجمعة'
        };
        return daysMap[day] || day;
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل أيام العمل...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">أيام العمل</h4>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/doctor/clinics')}
                    >
                        <FaArrowLeft className="mx-2" />
                        رجوع
                    </button>
                </div>

                {/* Work Days */}
                <div className="card shadow-sm">
                    <div className="card-body">
                        {workDays.length === 0 ? (
                            <p className="text-muted text-center">لا توجد أيام عمل</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th className="text-center">اليوم</th>
                                            <th className="text-center">من</th>
                                            <th className="text-center">إلى</th>
                                            <th className="text-center">مدة الموعد</th>
                                            <th className="text-center">العيادة</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {workDays.map((day, index) => (
                                            <motion.tr
                                                key={day.id || index}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="table-row-modern"
                                            >
                                                <td className="text-center">
                                                    <div className="order-number-badge">
                                                        #{index + 1}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {getDayNameInArabic(day.day)}
                                                </td>
                                                <td className="text-center">
                                                    {day.from}
                                                </td>
                                                <td className="text-center">
                                                    {day.to}
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge bg-warning text-dark">
                                                        {day.slot_duration || '30'} دقيقة
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge bg-info">
                                                        {day.clinic || 'غير محدد'}
                                                    </span>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DoctorWorkDays;

// CSS for table styling
const style = document.createElement('style');
style.textContent = `
    .table-modern {
        border-collapse: separate;
        border-spacing: 0;
    }
    
    .table-modern th {
        background: transparent;
        color: #374151;
        font-weight: 600;
        border: none;
        padding: 15px 10px;
        font-size: 0.9rem;
        text-align: center;
    }
    
    .table-modern td {
        padding: 15px 10px;
        border-bottom: 1px solid #e9ecef;
        vertical-align: middle;
        text-align: center;
    }
    
    .table-row-modern:hover {
        background-color: rgba(111, 66, 193, 0.08);
        transform: translateY(-1px);
        transition: all 0.2s ease;
        box-shadow: 0 2px 8px rgba(111, 66, 193, 0.1);
    }
    
    .order-number-badge {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-weight: 600;
        font-size: 0.875rem;
        display: inline-block;
        min-width: 60px;
        text-align: center;
        box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
    }
    
    .badge {
        font-size: 0.75rem;
        padding: 0.4em 0.8em;
    }
    
    /* Center icons with text */
    .table-modern td .d-flex {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
    }
    
    .table-modern td svg {
        margin: 0 0.25rem;
    }
`;
document.head.appendChild(style);

